/**
 * pSEO copy per variant. Sholat focuses on the 5 daily prayers and devotion;
 * Imsakiyah focuses on Ramadan fasting (sahur + iftar).
 */

import type { Location } from "@/data/locations";
import { findKotaBySlug, getProvinsi } from "@/data/locations";

export type PrayerVariant = "sholat" | "imsak" | "buka-puasa";

/** Strip "Kabupaten " / "Kab. " / "Kota " prefix so the H1 reads naturally. */
function stripAdminPrefix(name: string): string {
  return name.replace(/^(Kabupaten|Kab\.?|Kota)\s+/i, "").trim();
}

/** Fallback humanizer: turn slug like "kabupaten-cirebon" into "Cirebon". */
function humanizeSlug(slug: string): string {
  const cleaned = slug.replace(/^(kabupaten|kab|kota)-/i, "");
  return cleaned
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/**
 * Display name for a location: kecamatan shows "Kec, Kota", everything else
 * shows the bare name. Used in H1, <title>, og:title, JSON-LD, PDF titles.
 */
export function displayLocationTitle(loc: Location): string {
  if (loc.kind === "kecamatan") {
    const kota = loc.parentSlug ? findKotaBySlug(loc.parentSlug) : undefined;
    const cityName = kota
      ? stripAdminPrefix(kota.name)
      : loc.parentSlug
        ? humanizeSlug(loc.parentSlug)
        : "";
    return cityName ? `${loc.name}, ${cityName}` : loc.name;
  }
  return loc.name;
}

export interface PrayerCopy {
  introHeading: string;
  introBody: string;
  tipsHeading: string;
  tips: string[];
  faqs: { q: string; a: string }[];
}

export function getPrayerCopy(variant: PrayerVariant, loc: Location): PrayerCopy {
  const provName = getProvinsi(loc.provinceSlug)?.name ?? "Indonesia";
  const display = displayLocationTitle(loc);
  const kindLabel = loc.kind === "provinsi" ? "Provinsi" : "";
  const longName = kindLabel ? `${kindLabel} ${display}` : display;
  const isKec = loc.kind === "kecamatan";
  const cityName = isKec
    ? (loc.parentSlug
        ? (findKotaBySlug(loc.parentSlug) ? stripAdminPrefix(findKotaBySlug(loc.parentSlug)!.name) : humanizeSlug(loc.parentSlug))
        : "")
    : "";
  const kecExtra = isKec
    ? ` Khusus untuk warga ${display}, perhitungan menggunakan koordinat kecamatan (bukan pusat ${cityName}) sehingga selisih waktu Maghrib dan Subuh bisa berbeda 5–20 detik dari jadwal pusat kota.`
    : "";
  const kecFaq = isKec
    ? [{
        q: `Apakah jadwal di ${display} berbeda dari pusat ${cityName}?`,
        a: `Beda tipis. Karena ${loc.name} berada di koordinat ${loc.lat.toFixed(2)}°LS / ${loc.lng.toFixed(2)}°BT, selisih dengan jadwal pusat ${cityName} hanya 5–20 detik untuk Subuh, Maghrib, dan Isya — masih jauh di bawah ihtiyat 2 menit.`,
      }]
    : [];

  if (variant === "sholat") {
    return {
      introHeading: `Jadwal Sholat 5 Waktu di ${longName}`,
      introBody:
        `Jadwal sholat ${display}${loc.kind !== "provinsi" ? `, ${provName}` : ""} berisi waktu Imsak, Subuh, Terbit, Dhuha, Dzuhur, Ashar, Maghrib, dan Isya. ` +
        `Perhitungan menggunakan kriteria resmi Kementerian Agama Republik Indonesia (Subuh sudut 20°, Isya 18°, Asar mazhab Syafi'i, ihtiyat 2 menit). ` +
        `Selisih dengan jadwal Kemenag dan masjid setempat umumnya tidak lebih dari 1 menit, sehingga aman dijadikan acuan ibadah harian, jadwal adzan, hingga pengingat di kalender pribadi. ` +
        `Tabel jadwal di atas menampilkan jam sholat untuk hari ini dan satu bulan penuh — dapat diunduh sebagai PDF, ditempel di masjid, atau dibagikan ke grup keluarga. ` +
        `Khusus untuk warga ${display}, koordinat ${loc.lat.toFixed(2)}°LS / ${loc.lng.toFixed(2)}°BT digunakan agar perhitungan matahari sesuai geografi setempat. ` +
        `Anda juga bisa berpindah cepat ke kota atau kecamatan tetangga lewat indeks lokasi di bawah, atau menjadikan ${display} sebagai lokasi default agar selalu tampil pertama saat membuka jadwal sholat.` +
        kecExtra,
      tipsHeading: "Tips menjaga sholat 5 waktu",
      tips: [
        "Pasang alarm 5 menit sebelum adzan agar sempat berwudhu dan menuju masjid.",
        "Gunakan kompas atau aplikasi kiblat untuk memastikan arah sholat tepat.",
        "Manfaatkan waktu Dhuha (15 menit setelah terbit) untuk sholat sunnah.",
        "Jangan menunda sholat — utamakan di awal waktu agar lebih utama pahalanya.",
        "Aktifkan notifikasi adzan offline di ponsel agar tetap berbunyi tanpa internet.",
        "Untuk wanita haid, tetap baca jadwal sebagai panduan waktu istirahat & memasak.",
        "Saat safar, bandingkan dengan jadwal di kota tujuan — selisih bisa 5–15 menit.",
        "Cetak jadwal bulanan dan tempel di mushola rumah agar seluruh keluarga bisa cek bareng.",
      ],
      faqs: [
        ...kecFaq,
        {
          q: `Dari mana sumber jadwal sholat ${display}?`,
          a: "Perhitungan otomatis berbasis koordinat lokasi menggunakan algoritma astronomi standar (NREL/SPA) dengan parameter Kementerian Agama RI. Selisih dengan jadwal resmi Kemenag (SIHAT) biasanya kurang dari 1 menit.",
        },
        {
          q: "Apa beda waktu Imsak dan Subuh?",
          a: "Imsak adalah peringatan 10 menit sebelum Subuh agar makan sahur segera dihentikan. Sholat Subuh sendiri baru sah dilakukan setelah masuk waktu Subuh (fajar shadiq).",
        },
        {
          q: "Apakah jadwal ini sama dengan jadwal masjid setempat?",
          a: `Hampir sama. Selisih maksimum 1–2 menit karena perbedaan koordinat persis dan kebijakan ihtiyat lokal. Untuk pelaksanaan sholat berjamaah, ikuti adzan masjid terdekat di ${display}.`,
        },
        {
          q: "Apakah jadwal akurat sampai ke level RT atau perumahan?",
          a: `Ya. Jarak beberapa kilometer di dalam ${display} hanya menggeser waktu sholat sekitar 5–20 detik, jauh di bawah ihtiyat 2 menit yang sudah ditambahkan.`,
        },
        {
          q: "Bagaimana metode hisab Kemenag menghitung Subuh dan Isya?",
          a: "Subuh ditetapkan saat matahari berada 20° di bawah ufuk timur (fajar shadiq), Isya saat matahari 18° di bawah ufuk barat. Asar memakai mazhab Syafi'i (bayangan = panjang benda + bayangan zawal).",
        },
        {
          q: "Bagaimana jika saya pindah kota?",
          a: "Gunakan tombol Pengaturan → Cari kota/kecamatan, atau klik Gunakan lokasi saya di bagian bawah halaman untuk pindah otomatis ke kota terdekat.",
        },
      ],
    };
  }
  if (variant === "buka-puasa") {
    return {
      introHeading: `Waktu Buka Puasa dan Maghrib di ${longName}`,
      introBody:
        `Jadwal buka puasa ${display}${loc.kind !== "provinsi" ? `, ${provName}` : ""} berfokus pada waktu Maghrib sebagai momen iftar, yaitu saat puasa harian resmi berakhir. ` +
        `Data dihitung dari koordinat ${loc.lat.toFixed(2)}°LS / ${loc.lng.toFixed(2)}°BT, zona ${loc.tz}, memakai parameter Kementerian Agama RI: Subuh 20°, Isya 18°, Asar Syafi'i, dan ihtiyat 2 menit. ` +
        `Kolom Imsak dan Subuh disertakan untuk konteks sahur, sedangkan Isya membantu perencanaan Tarawih, tadarus, dan agenda Ramadan malam hari. ` +
        `Untuk warga ${display}, halaman ini cocok dipakai sebagai rujukan cepat menjelang azan Maghrib, dibagikan ke grup keluarga, masjid, musala, komunitas kajian, atau dicetak sebagai PDF bulanan. ` +
        `Karena waktu Maghrib dipengaruhi posisi matahari di ufuk barat, halaman kecamatan dapat memberi pembacaan yang lebih presisi dibanding hanya memakai titik pusat provinsi atau kota besar.` +
        kecExtra,
      tipsHeading: `Panduan berbuka puasa di ${display}`,
      tips: [
        "Segerakan berbuka saat Maghrib masuk; cukup batalkan puasa dengan air putih atau kurma sebelum makan besar.",
        "Gunakan kolom Maghrib sebagai acuan utama iftar, lalu beri jeda untuk sholat Maghrib sebelum makan berat.",
        "Jika memasak untuk keluarga atau masjid, mulai persiapan 30–45 menit sebelum Maghrib agar tidak terburu-buru.",
        "Cek ulang zona waktu WIB/WITA/WIT saat bepergian antarkota karena selisih Maghrib bisa mencapai puluhan menit.",
        "Untuk agenda buka bersama, pakai jadwal kecamatan terdekat dari lokasi acara, bukan kota asal undangan.",
        "Simpan PDF bulanan agar panitia takjil, DKM, dan keluarga punya referensi yang sama selama Ramadan.",
        "Tetap ikuti pengumuman resmi Kemenag atau masjid setempat untuk awal Ramadan, Idul Fitri, dan keputusan rukyatul hilal.",
      ],
      faqs: [
        ...kecFaq,
        {
          q: `Kapan waktu buka puasa di ${display}?`,
          a: `Waktu buka puasa adalah saat Maghrib masuk di ${display}. Lihat kolom Maghrib pada tabel halaman ini untuk jam berbuka hari ini dan jadwal sebulan penuh.`,
        },
        {
          q: "Apakah buka puasa sama dengan adzan Maghrib?",
          a: "Ya. Dalam praktik umum di Indonesia, puasa dibatalkan ketika waktu Maghrib masuk atau adzan Maghrib dikumandangkan. Jadwal ini menghitung masuknya Maghrib berdasarkan posisi matahari dan ihtiyat Kemenag RI.",
        },
        {
          q: "Mengapa jadwal buka puasa antar kecamatan bisa berbeda?",
          a: `Perbedaan koordinat timur-barat dan ketinggian ufuk membuat Maghrib di tiap lokasi dapat bergeser beberapa detik hingga sekitar 1–2 menit. Karena itu halaman ${display} memakai titik koordinat lokasi setempat.`,
        },
        {
          q: "Apakah jadwal ini bisa dipakai untuk puasa sunnah?",
          a: "Bisa. Waktu berbuka puasa sunnah Senin-Kamis, Ayyamul Bidh, Syawal, Arafah, dan Asyura tetap mengikuti masuknya Maghrib di lokasi Anda.",
        },
        {
          q: "Apa sumber metode perhitungannya?",
          a: "Perhitungan mengikuti parameter Kementerian Agama RI dan algoritma astronomi berbasis koordinat. Untuk keputusan ibadah berskala nasional seperti awal Ramadan dan Idul Fitri, tetap cek pengumuman resmi Kemenag.",
        },
        {
          q: "Apa doa berbuka puasa yang dianjurkan?",
          a: "Doa berbuka populer: \"Allahumma laka shumtu wa bika aamantu wa 'ala rizqika afthartu birahmatika ya arhamar raahimiin\" — Ya Allah, untuk-Mu aku berpuasa, kepada-Mu aku beriman, dan dengan rezeki-Mu aku berbuka.",
        },
        {
          q: "Bolehkah berbuka sebelum azan Maghrib jika sudah dengar azan dari masjid lain?",
          a: `Sebaiknya tunggu hingga Maghrib masuk di lokasi Anda (${display}). Azan dari masjid yang jauh bisa berbeda 1–3 menit dari posisi matahari di tempat Anda.`,
        },
        {
          q: "Apa beda jadwal Muhammadiyah dengan Kemenag?",
          a: "Untuk waktu sholat harian (termasuk Maghrib/buka puasa), perhitungan astronomi keduanya sangat mirip — selisih biasanya di bawah 1 menit. Perbedaan menonjol justru pada penetapan awal Ramadan/Idul Fitri (hisab wujudul hilal vs imkanur rukyat).",
        },
        {
          q: "Apakah air zamzam atau air biasa membatalkan puasa sebelum Maghrib?",
          a: "Ya, segala makanan dan minuman membatalkan puasa jika masuk ke tubuh secara sengaja sebelum waktu Maghrib. Berbuka hanya sah setelah Maghrib masuk.",
        },
      ],
    };
  }
  return {
    introHeading: `Jadwal Imsakiyah Ramadan di ${longName}`,
    introBody:
      `Jadwal imsakiyah ${display}${loc.kind !== "provinsi" ? `, ${provName}` : ""} dirancang khusus untuk umat Muslim yang menjalankan ibadah puasa Ramadan. ` +
      `Tabel berisi waktu Imsak (batas akhir sahur), Subuh, Maghrib (waktu berbuka puasa), serta Isya untuk sholat Tarawih. ` +
      `Imsak ditetapkan 10 menit sebelum waktu Subuh sebagai kehati-hatian (ihtiyat) sesuai aturan Kementerian Agama RI, agar makan dan minum sahur dihentikan sebelum fajar benar-benar terbit. ` +
      `Berbuka puasa dilakukan tepat saat adzan Maghrib berkumandang. Gunakan jadwal ini untuk merencanakan menu sahur, ifthar, jadwal tarawih, dan istirahat selama bulan suci. ` +
      `Doa berbuka yang dianjurkan: "Allahumma laka shumtu wa 'ala rizqika afthartu" — Ya Allah, untuk-Mu aku berpuasa dan dengan rezeki-Mu aku berbuka. ` +
      `Niat puasa Ramadan dibaca pada malam hari sebelum imsak: "Nawaitu shauma ghadin 'an adaa'i fardhi syahri Ramadhana hadzihis-sanati lillahi ta'ala."` +
      kecExtra,
    tipsHeading: "Tips menjalani puasa Ramadan",
    tips: [
      "Bangun sahur 30–45 menit sebelum imsak agar makan tidak tergesa-gesa.",
      "Konsumsi karbohidrat kompleks dan protein saat sahur untuk stamina seharian.",
      "Berbuka dengan kurma dan air putih sebelum melanjutkan makan utama.",
      "Sediakan jadwal imsakiyah di kulkas atau ponsel agar mudah diakses keluarga.",
      "Hindari minuman manis berlebih saat berbuka — pilih air putih, kurma, lalu makan utama setelah Maghrib.",
      "Sediakan camilan ringan tinggi serat untuk sahur (oat, pisang) agar kenyang lebih lama.",
      "Atur waktu tarawih + tadarus tepat setelah Isya agar tidur tidak terlalu malam.",
      "Cetak jadwal sebulan penuh dan tempel di dapur untuk memudahkan persiapan menu sahur & berbuka.",
    ],
    faqs: [
      ...kecFaq,
      {
        q: "Mengapa imsak ditetapkan 10 menit sebelum Subuh?",
        a: "Aturan Kemenag RI menetapkan imsak sebagai ihtiyat (kehati-hatian) agar umat menghentikan makan sahur sebelum waktu Subuh masuk. Ini bukan waktu wajib, tapi sangat dianjurkan.",
      },
      {
        q: `Kapan waktu berbuka puasa di ${display}?`,
        a: "Saat adzan Maghrib berkumandang, yaitu ketika seluruh piringan matahari telah tenggelam di ufuk barat. Lihat kolom Maghrib pada tabel jadwal di atas untuk waktu pastinya.",
      },
      {
        q: "Apakah jadwal imsakiyah ini berlaku setiap tahun?",
        a: "Jadwal otomatis menyesuaikan tanggal yang dipilih, jadi bisa digunakan untuk Ramadan tahun mana pun. Pilih bulan Ramadan pada navigasi tabel untuk melihat jadwal sebulan penuh.",
      },
      {
        q: "Bagaimana jika saya bepergian saat Ramadan?",
        a: `Gunakan jadwal kota tujuan, bukan jadwal ${display}. Anda boleh tidak puasa saat safar (perjalanan jauh), tapi wajib mengganti (qadha) di luar Ramadan.`,
      },
      {
        q: "Apakah jadwal ini sudah termasuk i'lam Lailatul Qadar?",
        a: "Tabel menampilkan badge khusus untuk hari-hari penting Ramadan termasuk awal 10 malam terakhir, Nuzulul Quran (17 Ramadan), dan Idul Fitri.",
      },
    ],
  };
}
